import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { UserData } from "@/entities/users/types";
import { getAcronyms } from "@/utilities";
import { cn } from "@/utils/utils";
import { User } from "@dreamhub/designsystem/icons";
import { FC } from "react";
import IconContainer from "@/components/ui/icon-container";

export type AvatarSize = "S" | "M" | "L" | "XL" | "XXL";

// `identity` is the design system's avatar: purple ground under a light-to-dark
// overlay, initials in white. `default` keeps the neutral fallback every
// existing caller renders today, so opting in is explicit.
export type AvatarVariant = "default" | "identity";

const sizeVariants: Record<AvatarSize, string> = {
  S:   "size-[18px] rounded-md",
  M:   "size-[22px] rounded-lg",
  L:   "size-7 rounded-[10px] !border-[1px]",
  XL:  "size-8 rounded-xl !border-[1px]",
  XXL: "size-10 rounded-2xl !border-[1px]",
};

// The design specifies 10px initials in the 18px box. The rest scale from that
// ratio rather than inheriting `0.8em`, which sizes off whatever text happens
// to surround the avatar instead of off the avatar itself.
const identityTextSize: Record<AvatarSize, string> = {
  S:   "text-[10px] leading-[14px]",
  M:   "text-[12px] leading-[16px]",
  L:   "text-[14px] leading-[18px]",
  XL:  "text-[16px] leading-[20px]",
  XXL: "text-[18px] leading-[22px]",
};

// Both backgrounds are typed explicitly — `color:` for the ground and `image:`
// for the overlay. An untyped `bg-[var(...)]` is ambiguous to tailwind-merge,
// which then treats it as conflicting with the gradient and drops one of them.
// The `dark:` prefix is load-bearing too: the base fallback sets
// `dark:bg-surface-avatar-fallback`, and a same-variant class is what displaces it.
// `--avatar-identity` is the semantic token; the palette shade behind it is not
// the API. See the design system's DESIGN.md > Components > Avatars.
const IDENTITY_SURFACE = cn(
  "bg-[color:var(--avatar-identity)] dark:bg-[color:var(--avatar-identity)]",
  "bg-[image:linear-gradient(to_bottom,rgba(250,252,253,0.16),rgba(12,17,29,0.24))]",
  "font-semibold text-white"
);

// Only these two fields are read, so the prop asks for only these two. Every
// caller passing a whole `UserData` still satisfies it, and a caller with just
// a display name no longer has to assert a full one it does not have.
type AvatarUserData = Pick<UserData, "displayName" | "avatarUrl">;

interface Props {
  dataTest?: string;
  userData?: AvatarUserData;
  size?: AvatarSize;
  variant?: AvatarVariant;
  className?: string;
  style?: React.CSSProperties;
  fallbackClassName?: string;
}

const UserAvatar: FC<Props> = ({
  dataTest,
  userData,
  size = "L",
  variant = "default",
  className,
  style,
  fallbackClassName
}) => {
  const isIdentity = variant === "identity";

  return (
    <Avatar
      data-test={dataTest || ""}
      className={cn(
        "relative",
        sizeVariants[size],
        isIdentity && "border-[color:var(--on-primary)]",
        className
      )}
      style={style}
    >
      {userData && <AvatarImage src={userData.avatarUrl ?? ""} />}
      <AvatarFallback
        className={cn(
          "leading-none -translate-x-[0.5px] items-center",
          isIdentity ? identityTextSize[size] : "text-[0.8em]",
          isIdentity && IDENTITY_SURFACE,
          // Still last, so a caller can override any of the above.
          fallbackClassName
        )}
      >
        {userData ? (
          getAcronyms(userData.displayName)
        ) : (
          <IconContainer size="5" className="p-1">
            <User />
          </IconContainer>
        )}
      </AvatarFallback>
    </Avatar>
  );
};

export default UserAvatar;
